/**
 * LiveKitContext.js — Drop-in replacement for WebRTCContext.js
 * Uses LiveKit SFU for audio/video transport.
 * Keeps existing WebSocket signaling for: whiteboard, chat, polls, hands, breakouts.
 */
import { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { Room, RoomEvent, Track, VideoPresets, ConnectionState } from 'livekit-client';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';
const WS_BASE = (() => {
  const url = process.env.REACT_APP_BACKEND_URL || '';
  return url.replace(/^http/, 'ws');
})();

const LiveKitCtx = createContext(null);
export const useWebRTC = () => useContext(LiveKitCtx);

export const WebRTCProvider = ({ children }) => {
  // State — same interface as old WebRTCContext
  const [isJoined, setIsJoined] = useState(false);
  const [currentRoom, setCurrentRoom] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [remoteStreams, setRemoteStreams] = useState({});
  // { pid, name } when a peer is sharing their screen — drives the "N is
  // presenting" pill in the classroom UI so students know who to look at.
  const [remoteScreenShare, setRemoteScreenShare] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [raisedHands, setRaisedHands] = useState([]);
  const [activePoll, setActivePoll] = useState(null);
  const [roomState, setRoomState] = useState(null);
  const [whiteboardGrant, setWhiteboardGrant] = useState('off'); // "off" | "all" | <user_id>
  const [error, setError] = useState(null);
  const [activeTeacherView, setActiveTeacherView] = useState('video');
  const [isRecording, setIsRecording] = useState(false);
  const [breakoutGroups, setBreakoutGroups] = useState({});
  const [studentControlsEnabled, setStudentControlsEnabled] = useState(true);
  const [presenceCount, setPresenceCount] = useState(0);
  const [typingUsers, setTypingUsers] = useState([]);

  // Refs
  const lkRoomRef = useRef(null);
  const wsRef = useRef(null);
  const userIdRef = useRef(null);
  const roomIdRef = useRef(null);
  const userNameRef = useRef(null);
  const userRoleRef = useRef(null);
  const whiteboardHandlerRef = useRef(null);
  const initialStrokesRef = useRef([]);
  const sessionEndedHandlerRef = useRef(null);
  const intentionalLeaveRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const heartbeatRef = useRef(null);
  // WS-sourced participant metadata (admin flags, hand state) keyed by participant id
  const wsParticipantDataRef = useRef({});
  // Cache of remote MediaStreams keyed by participant identity. Reused
  // across track events so audio elements never see srcObject churn.
  const remoteStreamsCacheRef = useRef({});
  // Audio: remote mic playback uses the browser's native pipeline via
  // <audio> element `.volume` (0..1). A Web Audio API graph was
  // previously wired here to allow > 1.0× boost, but routing WebRTC
  // audio through a MediaStreamAudioDestinationNode with a suspended
  // AudioContext (which is the default state until the user gestures)
  // produced silence — that was the "can't hear each other" symptom.
  //
  // Simpler + always-audible: control the audio element `.volume`
  // directly. Users can also use their OS volume control for further
  // amplification. AGC on capture (see Room config below) normalises
  // soft speakers at the source, which is the biggest quality win.
  const [remoteAudioVolume, setRemoteAudioVolumeState] = useState(1.0);
  const setRemoteAudioBoost = useCallback((value) => {
    // Kept for API compatibility with the classroom slider — clamp to
    // 0..1 range (browsers cap element .volume at 1.0). Values above
    // 1.0 are stored as 1.0 so the slider still moves visually but the
    // audio pipeline always plays.
    const clamped = Math.max(0, Math.min(1, Number(value) || 0));
    setRemoteAudioVolumeState(clamped);
  }, []);

  // ============================================================
  // HELPERS
  // ============================================================
  const wsSendQueueRef = useRef([]);
  const wsSend = useCallback((data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Drain any queued packets first so order is preserved.
      const q = wsSendQueueRef.current;
      if (q.length > 0) {
        for (const pending of q) {
          try { ws.send(JSON.stringify(pending)); } catch { /* ignore — connection may have died */ }
        }
        wsSendQueueRef.current = [];
      }
      try { ws.send(JSON.stringify(data)); } catch { wsSendQueueRef.current.push(data); }
      return;
    }
    // WebSocket isn't OPEN (CONNECTING / CLOSING / CLOSED) — queue the
    // packet so it can be replayed once the socket reconnects, instead of
    // silently dropping it. Without this, teachers' whiteboard strokes
    // were being lost during transient network blips and showing up as
    // missing fragments on the student side.
    wsSendQueueRef.current.push(data);
    // Cap the queue so a long disconnect doesn't blow memory.
    if (wsSendQueueRef.current.length > 500) {
      wsSendQueueRef.current.splice(0, wsSendQueueRef.current.length - 500);
    }
  }, []);

  const updateParticipantsList = useCallback(() => {
    const room = lkRoomRef.current;
    if (!room) return;
    const wsData = wsParticipantDataRef.current;
    const parts = [];
    // Local
    const lp = room.localParticipant;
    if (lp) {
      // Local id is the Darasani user id (userIdRef), not LiveKit's suffixed
      // identity. This keeps chat/whiteboard/polls matching across clients.
      const localAppId = userIdRef.current || lp.identity;
      const wsMeta = wsData[localAppId] || {};
      parts.push({
        id: localAppId,
        livekit_identity: lp.identity,
        name: lp.name || lp.identity,
        role: userRoleRef.current || 'student',
        isLocal: true,
        audioEnabled: lp.isMicrophoneEnabled,
        videoEnabled: lp.isCameraEnabled,
        has_audio: lp.isMicrophoneEnabled,
        has_video: lp.isCameraEnabled,
        is_muted_by_admin: wsMeta.is_muted_by_admin || false,
        is_video_off_by_admin: wsMeta.is_video_off_by_admin || false,
        hand_raised: wsMeta.hand_raised || false,
      });
    }
    // Remote
    room.remoteParticipants.forEach((rp) => {
      let role = 'student';
      let appUserId = rp.identity;
      try {
        const meta = JSON.parse(rp.metadata || '{}');
        role = meta.role || 'student';
        if (meta.user_id) appUserId = meta.user_id;
      } catch {}
      const wsMeta = wsData[appUserId] || wsData[rp.identity] || {};
      parts.push({
        id: appUserId,
        livekit_identity: rp.identity,
        name: rp.name || rp.identity,
        role,
        isLocal: false,
        audioEnabled: rp.isMicrophoneEnabled,
        videoEnabled: rp.isCameraEnabled,
        has_audio: rp.isMicrophoneEnabled,
        has_video: rp.isCameraEnabled,
        is_muted_by_admin: wsMeta.is_muted_by_admin || false,
        is_video_off_by_admin: wsMeta.is_video_off_by_admin || false,
        hand_raised: wsMeta.hand_raised || false,
      });
    });
    setParticipants(parts);
  }, []);

  const updateRemoteStreams = useCallback(() => {
    const room = lkRoomRef.current;
    if (!room) return;
    // -----------------------------------------------------------------
    // KEEP MEDIASTREAM IDENTITY STABLE
    // ---------------------------------------------------------------
    // Previously this rebuilt a fresh `new MediaStream(...)` for every
    // remote participant on every event (subscribe / unsubscribe / mute /
    // unmute / reconnect / participant join). The student's `<audio>`
    // element saw `srcObject` change each time, which forces the browser
    // to tear down its decoder and re-prime playback — that is what was
    // making the teacher's voice fade or briefly cut out for students.
    //
    // Fix: cache one MediaStream per remote identity. When new tracks
    // appear, *add* them to the cached stream; when they disappear,
    // *remove* them. The same MediaStream object is returned to React,
    // and the audio element keeps its decoder + buffer warm. Mute / unmute
    // doesn't touch the track set at all (LiveKit toggles `track.enabled`
    // under the hood) so audio is now glitch-free in both directions.
    // -----------------------------------------------------------------
    const cache = remoteStreamsCacheRef.current;
    const nextStreams = {};
    const seenIds = new Set();
    let detectedScreenShare = null;

    room.remoteParticipants.forEach((rp) => {
      // Prefer Darasani user_id from metadata so entries don't collide with
      // ourselves (chat etc. are keyed off user_id, not the per-session
      // LiveKit identity we now mint).
      let appId = rp.identity;
      try { const m = JSON.parse(rp.metadata || '{}'); if (m.user_id) appId = m.user_id; } catch {}
      seenIds.add(appId);
      // Split tracks into (audio, camera, screenShare). When a peer is
      // sharing their screen, we want that to REPLACE the camera in the
      // main video area — otherwise the browser's MediaStream element
      // will only play one of them (unpredictable) and the shared screen
      // is effectively invisible to students.
      let audioTrack = null;
      let cameraTrack = null;
      let screenTrack = null;
      let screenAudioTrack = null;
      rp.trackPublications.forEach((pub) => {
        if (!pub.isSubscribed || !pub.track || !pub.track.mediaStreamTrack) return;
        const src = pub.source;
        if (src === Track.Source.ScreenShare) screenTrack = pub.track.mediaStreamTrack;
        else if (src === Track.Source.ScreenShareAudio) screenAudioTrack = pub.track.mediaStreamTrack;
        else if (src === Track.Source.Camera) cameraTrack = pub.track.mediaStreamTrack;
        else if (src === Track.Source.Microphone) audioTrack = pub.track.mediaStreamTrack;
      });
      // Compose the "main" stream for this peer: prefer screen-share video
      // when present, else camera. Always include the microphone audio so
      // voice keeps working whether they're on cam or presenting slides.
      const desiredTracks = [];
      if (screenTrack) desiredTracks.push(screenTrack);
      else if (cameraTrack) desiredTracks.push(cameraTrack);
      if (audioTrack) desiredTracks.push(audioTrack);
      if (screenAudioTrack) desiredTracks.push(screenAudioTrack);
      if (desiredTracks.length === 0) {
        if (cache[appId]) delete cache[appId];
        return;
      }
      let ms = cache[appId];
      if (!ms) {
        ms = new MediaStream();
        cache[appId] = ms;
      }
      const desiredIds = new Set(desiredTracks.map((t) => t.id));
      ms.getTracks().forEach((t) => {
        if (!desiredIds.has(t.id)) ms.removeTrack(t);
      });
      const presentIds = new Set(ms.getTracks().map((t) => t.id));
      desiredTracks.forEach((t) => {
        if (!presentIds.has(t.id)) ms.addTrack(t);
      });
      nextStreams[appId] = ms;
      if (screenTrack) detectedScreenShare = { pid: appId, name: rp.name || appId };
    });

    // Drop cache entries for participants that left.
    Object.keys(cache).forEach((id) => {
      if (!seenIds.has(id)) delete cache[id];
    });

    setRemoteStreams(nextStreams);
    setRemoteScreenShare(detectedScreenShare);
  }, []);

  const updateLocalStream = useCallback(() => {
    const room = lkRoomRef.current;
    if (!room) return;
    const lp = room.localParticipant;
    const tracks = [];
    lp.trackPublications.forEach((pub) => {
      if (pub.track && pub.track.mediaStreamTrack && pub.source !== Track.Source.ScreenShare && pub.source !== Track.Source.ScreenShareAudio) {
        tracks.push(pub.track.mediaStreamTrack);
      }
    });
    if (tracks.length > 0) {
      setLocalStream(new MediaStream(tracks));
    }
  }, []);

  // ============================================================
  // WEBSOCKET (for signaling: chat, whiteboard, polls, etc.)
  // ============================================================
  const connectSignalingWS = useCallback((roomId, userId, userName, userRole) => {
    if (wsRef.current) { wsRef.current.close(); }
    const wsUrl = `${WS_BASE}/api/ws/room/${roomId}?user_id=${userId}&user_name=${encodeURIComponent(userName)}&user_role=${userRole}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      // Send join message to register with signaling server
      ws.send(JSON.stringify({ type: 'join', user_id: userId, user_name: userName, role: userRole }));
      heartbeatRef.current = setInterval(() => { if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' })); }, 25000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        switch (data.type) {
          case 'room-joined':
            // Sent by the backend once per join. Payload includes any
            // whiteboard strokes drawn BEFORE this student joined so we can
            // replay them (otherwise a late joiner sees a blank canvas).
            if (Array.isArray(data.whiteboard_strokes) && data.whiteboard_strokes.length) {
              if (whiteboardHandlerRef.current) {
                whiteboardHandlerRef.current({ type: 'whiteboard-init', strokes: data.whiteboard_strokes });
              } else {
                initialStrokesRef.current = data.whiteboard_strokes;
              }
            }
            // Full room state is delivered inside `room_state` — reuse the
            // same normaliser as the `room-state` case.
            if (data.room_state) {
              const rs = data.room_state;
              setRoomState(rs);
              if (rs.whiteboard_grant !== undefined) setWhiteboardGrant(rs.whiteboard_grant);
              if (rs.current_view) setActiveTeacherView(rs.current_view);
              if (rs.chat_messages) setChatMessages(rs.chat_messages);
              if (rs.is_recording) setIsRecording(rs.is_recording);
            }
            // Redis-backed chat history: mid-class joiners on ANY worker
            // see the last ~100 messages instead of an empty pane.
            if (Array.isArray(data.chat_history) && data.chat_history.length) {
              setChatMessages(prev => (prev && prev.length ? prev : data.chat_history));
            }
            break;
          case 'room-state':
            setRoomState(data.state);
            // Store WS participant metadata for merging with LiveKit data
            if (data.state?.participants) {
              const wsMap = {};
              (data.state.participants || []).forEach(p => {
                wsMap[p.id] = {
                  is_muted_by_admin: p.is_muted_by_admin || false,
                  is_video_off_by_admin: p.is_video_off_by_admin || false,
                  hand_raised: p.hand_raised || false,
                  has_audio: p.has_audio || false,
                  has_video: p.has_video || false,
                  role: p.role || 'student',
                  name: p.name,
                };
              });
              wsParticipantDataRef.current = wsMap;
              updateParticipantsList();
            }
            if (data.state?.chat_messages) setChatMessages(data.state.chat_messages);
            if (data.state?.raised_hands) setRaisedHands(data.state.raised_hands);
            if (data.state?.active_poll) setActivePoll(data.state.active_poll);
            if (data.state?.is_recording) setIsRecording(data.state.is_recording);
            if (data.state?.breakout_groups) setBreakoutGroups(data.state.breakout_groups);
            if (data.state?.student_controls_enabled !== undefined) setStudentControlsEnabled(data.state.student_controls_enabled);
            if (data.state?.whiteboard_grant !== undefined) setWhiteboardGrant(data.state.whiteboard_grant);
            break;
          case 'participant-update':
            // Teacher admin controls broadcast updated participant list
            if (data.participants) {
              const wsMap = { ...wsParticipantDataRef.current };
              data.participants.forEach(p => {
                wsMap[p.id] = {
                  is_muted_by_admin: p.is_muted_by_admin || false,
                  is_video_off_by_admin: p.is_video_off_by_admin || false,
                  hand_raised: p.hand_raised || false,
                  has_audio: p.has_audio || false,
                  has_video: p.has_video || false,
                  role: p.role || 'student',
                  name: p.name,
                };
              });
              wsParticipantDataRef.current = wsMap;
              updateParticipantsList();
            }
            break;
          case 'chat':
            setChatMessages(prev => [...prev, data]);
            break;
          case 'presence-update':
            if (typeof data.count === 'number') setPresenceCount(data.count);
            break;
          case 'typing': {
            const uid = data.user_id;
            if (!uid || uid === userIdRef.current) break;
            setTypingUsers(prev => {
              const others = prev.filter(t => t.user_id !== uid);
              if (data.is_typing) {
                return [...others, {
                  user_id: uid,
                  user_name: data.user_name || 'Someone',
                  at: Date.now(),
                }];
              }
              return others;
            });
            break;
          }
          case 'hand-raised': case 'hand-lowered': case 'hand-raise-update':
            setRaisedHands(data.raised_hands || []);
            // Update hand_raised in wsParticipantData
            {
              const wsMap = { ...wsParticipantDataRef.current };
              const raisedIds = new Set((data.raised_hands || []).map(h => h.id));
              Object.keys(wsMap).forEach(pid => { wsMap[pid] = { ...wsMap[pid], hand_raised: raisedIds.has(pid) }; });
              wsParticipantDataRef.current = wsMap;
              updateParticipantsList();
            }
            break;
          case 'hand-dismissed':
            setRaisedHands(data.raised_hands || []);
            {
              const wsMap = { ...wsParticipantDataRef.current };
              const raisedIds = new Set((data.raised_hands || []).map(h => h.id));
              Object.keys(wsMap).forEach(pid => { wsMap[pid] = { ...wsMap[pid], hand_raised: raisedIds.has(pid) }; });
              wsParticipantDataRef.current = wsMap;
              updateParticipantsList();
            }
            break;
          case 'poll-created': case 'poll-voted': case 'poll-update':
            if (data.poll) setActivePoll(data.poll);
            else setActivePoll(null);
            break;
          case 'poll-ended':
            setActivePoll(null);
            break;
          case 'view-change':
            setActiveTeacherView(data.view || 'video');
            break;
          case 'recording-started': case 'recording-update':
            setIsRecording(data.is_recording !== undefined ? data.is_recording : true);
            break;
          case 'recording-stopped':
            setIsRecording(false);
            break;
          case 'breakout-created': case 'breakout-update':
            if (data.action === 'closed') setBreakoutGroups({});
            else setBreakoutGroups(data.groups || {});
            break;
          case 'breakout-closed':
            setBreakoutGroups({});
            break;
          case 'breakout-message': case 'breakout-broadcast':
            toast.info(`[Breakout] ${data.from || data.user_name}: ${data.text}`);
            break;
          case 'student-controls-changed': case 'student-controls-update':
            setStudentControlsEnabled(data.enabled);
            break;
          case 'admin-force-mute': {
            // Teacher muted/unmuted this student's mic
            const room = lkRoomRef.current;
            if (room && data.muted !== undefined) {
              (async () => {
                try {
                  await room.localParticipant.setMicrophoneEnabled(!data.muted);
                } catch (e) { console.warn('Force mute mic toggle failed:', e); }
              })();
              setIsAudioEnabled(!data.muted);
              // Update local WS data
              const uid = userIdRef.current;
              if (uid) {
                wsParticipantDataRef.current = { ...wsParticipantDataRef.current, [uid]: { ...(wsParticipantDataRef.current[uid] || {}), is_muted_by_admin: data.muted } };
              }
              toast.info(data.muted ? 'Your mic was muted by teacher' : 'Your mic was unmuted by teacher');
            }
            break;
          }
          case 'admin-force-video': {
            // Teacher turned off/on this student's camera
            const room = lkRoomRef.current;
            if (room && data.video_off !== undefined) {
              (async () => {
                try {
                  await room.localParticipant.setCameraEnabled(!data.video_off);
                } catch (e) { console.warn('Force video toggle failed:', e); }
              })();
              setIsVideoEnabled(!data.video_off);
              const uid = userIdRef.current;
              if (uid) {
                wsParticipantDataRef.current = { ...wsParticipantDataRef.current, [uid]: { ...(wsParticipantDataRef.current[uid] || {}), is_video_off_by_admin: data.video_off } };
              }
              toast.info(data.video_off ? 'Your camera was turned off by teacher' : 'Your camera was turned on by teacher');
            }
            break;
          }
          case 'admin-kicked':
            toast.error(data.reason || 'Removed from class');
            intentionalLeaveRef.current = true;
            leaveRoom();
            break;
          case 'session-ended':
            // Teacher/host explicitly ended the class from the /end HTTP
            // endpoint. Surface a clear, friendly message and let the host
            // page decide where to redirect the student.
            toast.info(data.message || 'The teacher has ended the class.');
            intentionalLeaveRef.current = true;
            try {
              if (sessionEndedHandlerRef.current) sessionEndedHandlerRef.current(data);
            } catch {}
            leaveRoom();
            break;
          // Whiteboard events
          case 'whiteboard-init':
          case 'whiteboard-draw':
          case 'whiteboard-stroke':
          case 'whiteboard-shape':
          case 'whiteboard-clear':
          case 'whiteboard-undo':
            if (whiteboardHandlerRef.current) whiteboardHandlerRef.current(data);
            else if (data.type === 'whiteboard-init') initialStrokesRef.current = data.strokes || [];
            break;
          case 'whiteboard-control':
            // Teacher transferred drawing permission. Update local state so
            // the WhiteboardCanvas component re-renders with the right tool
            // bar and can/cannot dispatch strokes.
            if (data.grant !== undefined) setWhiteboardGrant(data.grant || 'off');
            break;
          default:
            break;
        }
      } catch {}
    };

    ws.onclose = () => {
      clearInterval(heartbeatRef.current);
      if (!intentionalLeaveRef.current && roomIdRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          connectSignalingWS(roomIdRef.current, userIdRef.current, userNameRef.current, userRoleRef.current);
        }, 3000);
      }
    };
  }, []);

  // ============================================================
  // JOIN / LEAVE
  // ============================================================
  const joinRoom = useCallback(async (roomId, userId, userName, userRole, options = {}) => {
    try {
      intentionalLeaveRef.current = false;
      userIdRef.current = userId;
      roomIdRef.current = roomId;
      userNameRef.current = userName;
      userRoleRef.current = userRole;

      // 1. Get LiveKit token from backend
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/api/livekit/token`, {
        room_name: `darasani_${roomId}`,
        participant_name: userName,
      }, { headers: { Authorization: `Bearer ${token}` } });

      const { token: lkToken, url: lkUrl } = res.data;

      // 2. Create LiveKit Room
      // audioCaptureDefaults: keep echo cancellation + noise suppression AND
      // autoGainControl for a clean call experience. AGC is the #1 fix for
      // "teacher's voice is too quiet" — the browser auto-normalises the
      // speaker's output level regardless of how far they hold the mic. We
      // pair this with a client-side Web Audio GainNode below (1.6× boost)
      // for a final +4 dB nudge that most students find just right.
      // Bitrate bumped from 64 → 96 kbps for clearer speech.
      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
        videoCaptureDefaults: { resolution: VideoPresets.h720.resolution },
        audioCaptureDefaults: {
          autoGainControl: true,
          echoCancellation: true,
          noiseSuppression: true,
          channelCount: 1,
          sampleRate: 48000,
        },
        publishDefaults: {
          audioPreset: { maxBitrate: 96_000 },
          dtx: true,        // Discontinuous transmission — silent frames are skipped
          red: true,        // Redundant audio encoding — smoother on lossy nets
        },
      });
      lkRoomRef.current = room;

      // 3. Set up LiveKit event handlers
      room.on(RoomEvent.ParticipantConnected, (p) => { console.log('[LK-DIAG] Participant connected:', p.identity, p.name); updateParticipantsList(); updateRemoteStreams(); });
      room.on(RoomEvent.ParticipantDisconnected, (p) => { console.log('[LK-DIAG] Participant disconnected:', p.identity); updateParticipantsList(); updateRemoteStreams(); });
      // A remote participant PUBLISHED a track — this fires BEFORE we're
      // subscribed. Track subscription is automatic (autoSubscribe: true).
      room.on(RoomEvent.TrackPublished, (pub, participant) => {
        console.log('[LK-DIAG] Remote track published:', participant.identity, pub.kind, pub.trackSid, 'subscribed=', pub.isSubscribed);
        // Nudge subscription in case autoSubscribe is off or the flag was
        // dropped by a proxy/signaling glitch.
        try { pub.setSubscribed?.(true); } catch {}
      });
      room.on(RoomEvent.TrackSubscribed, (track, pub, participant) => {
        console.log('[LK-DIAG] Track SUBSCRIBED:', participant.identity, track.kind, track.sid);
        updateRemoteStreams();
        updateParticipantsList();
        // Newly subscribed audio track — nudge LiveKit to resume audio.
        // Some browsers gate autoplay per-element; this guarantees the
        // student hears the host the moment the track arrives.
        if (track?.kind === 'audio') {
          try { room.startAudio?.(); } catch {}
        }
      });
      room.on(RoomEvent.TrackSubscriptionFailed, (trackSid, participant) => {
        console.error('[LK-DIAG] Track subscription FAILED:', participant?.identity, trackSid);
        toast.error('Could not subscribe to a peer track. Reconnecting audio…');
        // Attempt manual re-subscription
        setTimeout(() => {
          try {
            participant?.trackPublications?.forEach((pub) => {
              if (!pub.isSubscribed) pub.setSubscribed?.(true);
            });
          } catch {}
          updateRemoteStreams();
        }, 1000);
      });
      room.on(RoomEvent.TrackUnsubscribed, (track, pub, participant) => {
        console.log('[LK-DIAG] Track unsubscribed:', participant?.identity, track.kind);
        updateRemoteStreams();
        updateParticipantsList();
      });
      // Mute/unmute does NOT change the set of subscribed tracks, only
      // their `enabled` flag — there is no need to rebuild MediaStreams.
      // (Doing so was forcing audio elements to re-prime and produced the
      // intermittent fade-outs students reported.) We only refresh the
      // participants list (UI mic icon) and the local stream label.
      room.on(RoomEvent.TrackMuted, () => { updateLocalStream(); updateParticipantsList(); });
      room.on(RoomEvent.TrackUnmuted, () => { updateLocalStream(); updateParticipantsList(); });
      room.on(RoomEvent.LocalTrackPublished, () => { updateLocalStream(); updateParticipantsList(); });
      room.on(RoomEvent.LocalTrackUnpublished, () => { updateLocalStream(); updateParticipantsList(); });
      room.on(RoomEvent.LocalTrackPublished, () => { updateLocalStream(); updateParticipantsList(); });
      room.on(RoomEvent.LocalTrackUnpublished, () => { updateLocalStream(); updateParticipantsList(); });
      room.on(RoomEvent.Disconnected, () => {
        if (!intentionalLeaveRef.current) {
          toast.error('Disconnected from LiveKit. Reconnecting...');
        }
      });
      room.on(RoomEvent.Reconnecting, () => { toast.info('Reconnecting...'); });
      room.on(RoomEvent.Reconnected, () => { toast.success('Reconnected!'); updateRemoteStreams(); updateParticipantsList(); });
      room.on(RoomEvent.ConnectionStateChanged, (state) => {
        if (state === ConnectionState.Connected) {
          updateParticipantsList();
          updateRemoteStreams();
          updateLocalStream();
        }
      });

      // 4. Connect to LiveKit — explicitly request autoSubscribe so a
      // misbehaving proxy/signalling handshake can never leave us in a
      // "connected but not subscribed" limbo. Also negotiate the ICE
      // transport so restrictive networks still work via TURN.
      console.log('[LK-DIAG] Connecting to', lkUrl, 'room=darasani_' + roomId, 'identity=', userId);
      await room.connect(lkUrl, lkToken, {
        autoSubscribe: true,
      });
      console.log('[LK-DIAG] Connected. State=', room.state, 'localSid=', room.localParticipant.sid);
      // Post-connect diagnostic dump — 3 seconds after we're in the room,
      // log EVERYTHING so we can debug "no audio" reports from users by
      // asking them to paste this from F12 console.
      setTimeout(() => {
        try {
          const rp = Array.from(room.remoteParticipants.values());
          console.log('[LK-DIAG] === STATE @ +3s ===');
          console.log('[LK-DIAG] connectionState:', room.state, 'canPlaybackAudio:', room.canPlaybackAudio);
          console.log('[LK-DIAG] localTracks:', Array.from(room.localParticipant.trackPublications.values()).map(p => ({ kind: p.kind, sid: p.trackSid, muted: p.isMuted, subscribed: p.isSubscribed })));
          console.log('[LK-DIAG] remoteParticipants:', rp.length, rp.map(p => ({
            identity: p.identity, name: p.name,
            tracks: Array.from(p.trackPublications.values()).map(t => ({ kind: t.kind, sid: t.trackSid, subscribed: t.isSubscribed, muted: t.isMuted, hasTrack: !!t.track })),
          })));
        } catch (e) { console.error('[LK-DIAG] dump failed:', e); }
      }, 3000);

      // Kick LiveKit's autoplay handling — many browsers block audio until
      // the user gestures. Room.startAudio() explicitly resumes audio for
      // ALL remote participants. We attach gesture listeners
      // UNCONDITIONALLY (not just on failure) because many browsers will
      // resolve startAudio() even though the individual <audio srcObject>
      // elements remain suspended until a user interaction. The listeners
      // keep retrying on every click/touch/keydown until audio actually
      // plays (canPlaybackAudio becomes true), then self-remove.
      const gestureUnlock = async () => {
        try { await room.startAudio?.(); } catch {}
        if (room.canPlaybackAudio) {
          document.removeEventListener('click', gestureUnlock, true);
          document.removeEventListener('touchstart', gestureUnlock, true);
          document.removeEventListener('keydown', gestureUnlock, true);
        }
      };
      try { await room.startAudio?.(); } catch {}
      document.addEventListener('click', gestureUnlock, true);
      document.addEventListener('touchstart', gestureUnlock, true);
      document.addEventListener('keydown', gestureUnlock, true);
      // AudioPlaybackStatusChanged also fires if the browser blocks audio
      // later; re-arm our listeners if needed.
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
        if (!room.canPlaybackAudio) {
          document.addEventListener('click', gestureUnlock, true);
          document.addEventListener('touchstart', gestureUnlock, true);
          document.addEventListener('keydown', gestureUnlock, true);
        }
      });

      // 5. Publish local tracks — audio-only for audio rooms, else mic+cam
      //    Strategy: publish mic IMMEDIATELY after connect. Do NOT do a
      //    separate getUserMedia warmup — that adds 2-5s of dead time during
      //    which we're in the room with ZERO tracks and peers see us as
      //    "empty participant" (they show "Waiting for…" indefinitely).
      //    LiveKit's setMicrophoneEnabled internally calls getUserMedia and
      //    triggers the browser permission prompt at the RIGHT moment.
      const audioOnly = !!options.audioOnly;

      let micPublished = false;
      // Try mic with up to 2 retries if the OS / browser is sluggish to grant.
      for (let attempt = 0; attempt < 3 && !micPublished; attempt++) {
        try {
          console.log('[LK-DIAG] mic publish attempt', attempt + 1);
          await room.localParticipant.setMicrophoneEnabled(true);
          const audioPub = Array.from(room.localParticipant.audioTrackPublications.values?.() || room.localParticipant.audioTracks?.values?.() || [])[0];
          if (audioPub) {
            micPublished = true;
            setIsAudioEnabled(true);
            console.log('[LK-DIAG] mic published:', audioPub.trackSid);
          } else if (attempt < 2) {
            console.warn('[LK-DIAG] setMicrophoneEnabled resolved but no publication; retrying');
            await new Promise(r => setTimeout(r, 400));
          }
        } catch (micErr) {
          console.error(`[LK-DIAG] mic publish attempt ${attempt + 1} failed:`, micErr?.name, micErr?.message);
          if (attempt < 2) await new Promise(r => setTimeout(r, 400));
        }
      }
      if (!micPublished) {
        setIsAudioEnabled(false);
        toast.error('Microphone unavailable. Tap the mic icon to retry.');
      }

      // Now publish camera (skip in audioOnly mode). Camera failure must NOT
      // block the user from having a working session — they can still hear /
      // be heard. We swallow camera errors.
      if (!audioOnly) {
        try {
          console.log('[LK-DIAG] camera publish attempt');
          await room.localParticipant.setCameraEnabled(true);
          const camPub = Array.from(room.localParticipant.videoTrackPublications?.values?.() || []).find(p => p.source === Track.Source.Camera);
          if (camPub) {
            setIsVideoEnabled(true);
            console.log('[LK-DIAG] camera published:', camPub.trackSid);
          } else {
            setIsVideoEnabled(false);
            console.warn('[LK-DIAG] setCameraEnabled resolved but no camera publication found');
          }
        } catch (camErr) {
          console.error('[LK-DIAG] camera publish failed:', camErr?.name, camErr?.message);
          setIsVideoEnabled(false);
          if (micPublished) toast.info('Camera unavailable — audio-only mode');
        }
      } else {
        setIsVideoEnabled(false);
      }

      updateParticipantsList();
      updateLocalStream();

      // 6. Connect signaling WebSocket (for chat, whiteboard, polls)
      connectSignalingWS(roomId, userId, userName, userRole);

      setCurrentRoom(roomId);
      setIsJoined(true);
      setError(null);
      toast.success('Joined class via LiveKit');
    } catch (err) {
      console.error('Failed to join LiveKit room:', err);
      setError(err.message);
      toast.error('Failed to join: ' + err.message);
    }
  }, [connectSignalingWS, updateParticipantsList, updateRemoteStreams, updateLocalStream]);

  const leaveRoom = useCallback(() => {
    intentionalLeaveRef.current = true;
    clearTimeout(reconnectTimerRef.current);
    clearInterval(heartbeatRef.current);

    // Disconnect LiveKit
    const room = lkRoomRef.current;
    if (room) {
      room.disconnect();
      lkRoomRef.current = null;
    }

    // Close signaling WS
    if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }

    setIsJoined(false);
    setCurrentRoom(null);
    setParticipants([]);
    setRemoteStreams({});
    setLocalStream(null);
    setScreenStream(null);
    setIsAudioEnabled(true);
    setIsVideoEnabled(true);
    setIsScreenSharing(false);
    setChatMessages([]);
    setRaisedHands([]);
    setActivePoll(null);
    setRoomState(null);
    setIsRecording(false);
    setBreakoutGroups({});
    wsParticipantDataRef.current = {};
  }, []);

  // ============================================================
  // MEDIA CONTROLS
  // ============================================================
  // Force-republish mic + cam. Exposed as a "Retry devices" button so
  // users can recover from failed initial publishes without leaving the
  // classroom (e.g. permissions granted late, USB mic re-plugged in).
  const retryPublish = useCallback(async () => {
    const room = lkRoomRef.current;
    if (!room) return { audio: false, video: false };
    console.log('[LK-DIAG] retryPublish called');
    let audio = false, video = false;
    try {
      const warmup = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      warmup.getTracks().forEach(t => t.stop());
    } catch (e) {
      console.error('[LK-DIAG] retry getUserMedia failed:', e?.message);
    }
    try {
      await room.localParticipant.setMicrophoneEnabled(true);
      audio = Array.from(room.localParticipant.audioTrackPublications.values()).length > 0;
      setIsAudioEnabled(audio);
    } catch (e) {
      console.error('[LK-DIAG] retry mic publish failed:', e?.message);
    }
    try {
      await room.localParticipant.setCameraEnabled(true);
      video = Array.from(room.localParticipant.videoTrackPublications.values()).some(p => p.source === Track.Source.Camera);
      setIsVideoEnabled(video);
    } catch (e) {
      console.error('[LK-DIAG] retry cam publish failed:', e?.message);
    }
    updateLocalStream();
    updateParticipantsList();
    if (audio || video) {
      toast.success(`Devices reconnected: ${audio ? 'mic' : ''}${audio && video ? ' + ' : ''}${video ? 'camera' : ''}`);
    } else {
      toast.error('Still no devices — check browser permissions (address bar 🔒)');
    }
    return { audio, video };
  }, [updateLocalStream, updateParticipantsList]);

  const toggleAudio = useCallback(async () => {
    const room = lkRoomRef.current;
    if (!room) return;
    const currentlyEnabled = room.localParticipant.isMicrophoneEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(!currentlyEnabled);
      setIsAudioEnabled(!currentlyEnabled);
    } catch (err) {
      console.error('Failed to toggle mic:', err);
      toast.error('Could not toggle microphone');
    }
  }, []);

  const toggleVideo = useCallback(async () => {
    const room = lkRoomRef.current;
    if (!room) return;
    const currentlyEnabled = room.localParticipant.isCameraEnabled;
    try {
      await room.localParticipant.setCameraEnabled(!currentlyEnabled);
      setIsVideoEnabled(!currentlyEnabled);
      setTimeout(updateLocalStream, 500);
    } catch (err) {
      console.error('Failed to toggle camera:', err);
      toast.error('Could not toggle camera');
    }
  }, [updateLocalStream]);

  const toggleScreenShare = useCallback(async () => {
    const room = lkRoomRef.current;
    if (!room) return;
    try {
      if (isScreenSharing) {
        await room.localParticipant.setScreenShareEnabled(false);
        setIsScreenSharing(false);
        setScreenStream(null);
      } else {
        await room.localParticipant.setScreenShareEnabled(true);
        setIsScreenSharing(true);
      }
    } catch (err) {
      console.error('Screen share error:', err);
      toast.error('Screen share failed');
    }
  }, [isScreenSharing]);

  // ============================================================
  // SIGNALING FEATURES (via WebSocket — unchanged)
  // ============================================================
  const sendChat = useCallback((text) => { wsSend({ type: 'chat', text }); }, [wsSend]);

  const toggleHandRaise = useCallback((raised) => {
    wsSend({ type: 'hand-raise', raised: raised !== undefined ? raised : true });
  }, [wsSend]);

  const dismissHand = useCallback((targetId) => {
    wsSend({ type: 'dismiss-hand', target_id: targetId });
  }, [wsSend]);

  const createPoll = useCallback((question, options) => {
    wsSend({ type: 'create-poll', question, options });
  }, [wsSend]);

  const votePoll = useCallback((optionIndex) => {
    wsSend({ type: 'vote-poll', option_index: optionIndex });
  }, [wsSend]);

  const endPoll = useCallback(() => { wsSend({ type: 'end-poll' }); }, [wsSend]);

  // Whiteboard
  const sendWhiteboardStroke = useCallback((stroke) => {
    wsSend({ type: 'whiteboard-stroke', stroke });
  }, [wsSend]);

  const sendWhiteboardDraw = useCallback((points, strokeId, done = false) => {
    wsSend({ type: 'whiteboard-draw', points, stroke_id: strokeId, done });
  }, [wsSend]);

  const clearWhiteboard = useCallback(() => { wsSend({ type: 'whiteboard-clear' }); }, [wsSend]);

  const sendWhiteboardShape = useCallback((shape) => {
    wsSend({ type: 'whiteboard-shape', shape, shape_id: shape?.id });
  }, [wsSend]);

  const sendWhiteboardUndo = useCallback((strokeId) => {
    wsSend({ type: 'whiteboard-undo', stroke_id: strokeId });
  }, [wsSend]);

  // Transfer drawing control. `grant` is "off" | "all" | <user_id>.
  // Only the teacher/host has authority (backend enforces this).
  const sendWhiteboardControl = useCallback((grant) => {
    wsSend({ type: 'whiteboard-control', grant: grant || 'off' });
  }, [wsSend]);

  const registerWhiteboardHandler = useCallback((handler) => {
    whiteboardHandlerRef.current = handler;
    if (handler && initialStrokesRef.current.length > 0) {
      handler({ type: 'whiteboard-init', strokes: initialStrokesRef.current });
      initialStrokesRef.current = [];
    }
  }, []);

  // Allows consumer pages (SessionRoom, AudioRooms, LiveClassDetail) to react
  // when the teacher/host ends the session — e.g. show a modal and redirect.
  const registerSessionEndedHandler = useCallback((handler) => {
    sessionEndedHandlerRef.current = handler;
  }, []);

  // View change
  const sendViewChange = useCallback((view) => {
    wsSend({ type: 'view-change', view });
    setActiveTeacherView(view);
  }, [wsSend]);

  // Admin controls
  const forceMuteParticipant = useCallback((targetId, muted) => {
    wsSend({ type: 'admin-force-mute', target_id: targetId, muted });
    // Optimistically update local WS data for instant UI feedback
    wsParticipantDataRef.current = {
      ...wsParticipantDataRef.current,
      [targetId]: { ...(wsParticipantDataRef.current[targetId] || {}), is_muted_by_admin: muted }
    };
    updateParticipantsList();
  }, [wsSend, updateParticipantsList]);

  const forceVideoParticipant = useCallback((targetId, videoOff) => {
    wsSend({ type: 'admin-force-video', target_id: targetId, video_off: videoOff });
    // Optimistically update local WS data for instant UI feedback
    wsParticipantDataRef.current = {
      ...wsParticipantDataRef.current,
      [targetId]: { ...(wsParticipantDataRef.current[targetId] || {}), is_video_off_by_admin: videoOff }
    };
    updateParticipantsList();
  }, [wsSend, updateParticipantsList]);

  const kickParticipant = useCallback((targetId, reason = 'Removed by teacher') => {
    wsSend({ type: 'admin-kick', target_id: targetId, reason });
  }, [wsSend]);

  // Breakout
  const createBreakoutGroups = useCallback((groups) => {
    wsSend({ type: 'breakout-create', groups });
  }, [wsSend]);

  const closeBreakoutGroups = useCallback(() => { wsSend({ type: 'breakout-close' }); }, [wsSend]);

  const sendBreakoutMessage = useCallback((text, groupId) => {
    wsSend({ type: 'breakout-message', text, group_id: groupId });
  }, [wsSend]);

  const reassignBreakoutMember = useCallback((userId, toGroupId) => {
    wsSend({ type: 'breakout-reassign', user_id: userId, to_group_id: toGroupId });
  }, [wsSend]);

  // Recording
  const startRecording = useCallback(() => { wsSend({ type: 'recording-start' }); }, [wsSend]);

  const stopRecording = useCallback((access = 'enrolled_only', videoUrl = null) => {
    wsSend({ type: 'recording-stop', access, video_url: videoUrl });
  }, [wsSend]);

  // Student controls
  const toggleStudentControls = useCallback((enabled) => {
    wsSend({ type: 'admin-toggle-student-controls', enabled });
    setStudentControlsEnabled(enabled);
  }, [wsSend]);

  // End session (teacher ends class for all)
  const endSession = useCallback((reason = 'Session ended by host') => {
    wsSend({ type: 'end-session', reason });
  }, [wsSend]);

  // Typing indicator sender — throttled by the caller.
  const sendTyping = useCallback((isTyping = true) => {
    wsSend({ type: 'typing', is_typing: !!isTyping });
  }, [wsSend]);

  // Auto-expire stale typing indicators (safety net if peer's `false`
  // message is dropped mid-stream).
  useEffect(() => {
    if (typingUsers.length === 0) return undefined;
    const timer = setInterval(() => {
      const now = Date.now();
      setTypingUsers(prev => prev.filter(t => now - (t.at || 0) < 3500));
    }, 1000);
    return () => clearInterval(timer);
  }, [typingUsers.length]);

  // Cleanup
  useEffect(() => { return () => { leaveRoom(); }; }, [leaveRoom]);

  // ============================================================
  // CONTEXT VALUE — same shape as old WebRTCContext
  // ============================================================
  const value = {
    isJoined, currentRoom, participants, remoteStreams, remoteScreenShare, localStream, screenStream,
    isAudioEnabled, isVideoEnabled, isScreenSharing,
    chatMessages, raisedHands, activePoll, roomState, error,
    activeTeacherView, isRecording, breakoutGroups,
    studentControlsEnabled, toggleStudentControls,
    whiteboardGrant, sendWhiteboardControl,
    joinRoom, leaveRoom, toggleAudio, toggleVideo, toggleScreenShare,
    retryPublish,
    sendChat, toggleHandRaise, dismissHand,
    createPoll, votePoll, endPoll,
    sendWhiteboardStroke, sendWhiteboardDraw, clearWhiteboard, sendWhiteboardShape,
    sendWhiteboardUndo,
    registerWhiteboardHandler, sendViewChange,
    registerSessionEndedHandler,
    forceMuteParticipant, forceVideoParticipant, kickParticipant,
    createBreakoutGroups, closeBreakoutGroups, sendBreakoutMessage, reassignBreakoutMember,
    startRecording, stopRecording,
    endSession,
    presenceCount, typingUsers, sendTyping,
    setRemoteAudioBoost,
    remoteAudioVolume,
  };

  return <LiveKitCtx.Provider value={value}>{children}</LiveKitCtx.Provider>;
};
